import type { ClinicalEditorApi } from './context';
import type {
  ClinicalTrialIntake,
  ClinicalTrialStudyDraft,
  ManagedClinicalTrial,
  ManagedStudyContact,
  PosterSubmissionCreate,
  PosterSubmissionResponse,
} from '@rudi/shared/types/clinical-trials';
export function createClinicalOperations(apiClient: ClinicalEditorApi) {
  async function updateManagedStudyCriteria(
    studyId: string,
    inclusionCriteria: string | null,
    exclusionCriteria: string | null,
    expectedVersion: number,
    investigatorSlug?: string | null
  ): Promise<ManagedClinicalTrial> {
    const { data } = await apiClient.patch<ManagedClinicalTrial>(
      managedStudyPath(studyId, 'criteria', investigatorSlug),
      { inclusionCriteria, exclusionCriteria, expectedVersion }
    );
    return data;
  }

  async function updateManagedStudyRecruitment(
    studyId: string,
    recruitmentStatusCode: string,
    siteId: string | null,
    expectedVersion: number,
    investigatorSlug?: string | null
  ): Promise<ManagedClinicalTrial> {
    const { data } = await apiClient.patch<ManagedClinicalTrial>(
      managedStudyPath(studyId, 'recruitment', investigatorSlug),
      { recruitmentStatusCode, siteId, expectedVersion }
    );
    return data;
  }

  async function updateManagedStudyShortTitle(
    studyId: string,
    shortTitle: string | null,
    expectedVersion: number,
    investigatorSlug?: string | null
  ): Promise<ManagedClinicalTrial> {
    const { data } = await apiClient.patch<ManagedClinicalTrial>(
      managedStudyPath(studyId, 'short-title', investigatorSlug),
      { shortTitle, expectedVersion }
    );
    return data;
  }

  async function updateManagedStudyContact(
    studyId: string,
    contact: ManagedStudyContact | null,
    siteId: string | null,
    expectedVersion: number,
    investigatorSlug?: string | null
  ): Promise<ManagedClinicalTrial> {
    const { data } = await apiClient.patch<ManagedClinicalTrial>(
      managedStudyPath(studyId, 'contact', investigatorSlug),
      { contact, siteId, expectedVersion }
    );
    return data;
  }

  async function updateManagedStudyVisibility(
    studyId: string,
    published: boolean,
    expectedVersion: number,
    investigatorSlug?: string | null
  ): Promise<ManagedClinicalTrial> {
    const { data } = await apiClient.patch<ManagedClinicalTrial>(
      managedStudyPath(studyId, 'visibility', investigatorSlug),
      { published, expectedVersion }
    );
    return data;
  }

  function managedStudyPath(
    studyId: string,
    action: string,
    investigatorSlug?: string | null
  ): string {
    if (!investigatorSlug) return `/clinical-trials/managed-studies/${studyId}/${action}`;
    return (
      `/clinical-trials/investigators/${encodeURIComponent(investigatorSlug)}` +
      `/managed-studies/${studyId}/${action}`
    );
  }

  async function assignStudyToInvestigator(
    investigatorSlug: string,
    studyId: string,
    siteId?: string | null
  ): Promise<ManagedClinicalTrial> {
    const { data } = await apiClient.post<ManagedClinicalTrial>(
      `/clinical-trials/investigators/${encodeURIComponent(investigatorSlug)}/study-assignments`,
      { studyId, siteId: siteId ?? null }
    );
    return data;
  }

  async function unassignStudyFromInvestigator(
    investigatorSlug: string,
    assignmentId: string
  ): Promise<void> {
    await apiClient.delete(
      `/clinical-trials/investigators/${encodeURIComponent(investigatorSlug)}` +
        `/study-assignments/${assignmentId}`
    );
  }

  async function createPosterSubmission(
    data: PosterSubmissionCreate
  ): Promise<PosterSubmissionResponse> {
    const { data: submission } = await apiClient.post<PosterSubmissionResponse>(
      '/clinical-trials/poster-submissions',
      data
    );
    return submission;
  }

  async function updateMedicalRepositoryStudy(
    study: ManagedClinicalTrial,
    investigatorSlug: string | null | undefined,
    fields: Record<string, unknown>
  ): Promise<ManagedClinicalTrial> {
    const slug = investigatorSlug || study.management.investigator_slug;
    if (!slug || !study.registry_revision)
      throw new Error('Select the study investigator before editing.');
    const { data } = await apiClient.patch<ManagedClinicalTrial>(
      `/clinical-trials/investigators/${encodeURIComponent(slug)}/medical-repository-studies/${encodeURIComponent(study.catalog_id)}`,
      { fields, expectedVersion: study.version, registryRevision: study.registry_revision }
    );
    return data;
  }

  async function createStudyIntake(
    draft: Partial<ClinicalTrialStudyDraft>,
    file?: File | null,
    agentHint?: string,
    investigatorSlug?: string | null
  ): Promise<ClinicalTrialIntake> {
    const body = new FormData();
    body.set('form_data', JSON.stringify(draft));
    if (file) body.set('file', file);
    if (agentHint?.trim()) body.set('agent_hint', agentHint.trim());
    const { data } = await apiClient.post<ClinicalTrialIntake>(
      investigatorSlug
        ? `/clinical-trials/investigators/${encodeURIComponent(investigatorSlug)}/study-intakes`
        : '/clinical-trials/study-intakes',
      body,
      {}
    );
    return data;
  }

  async function analyzeStudyIntake(intakeId: string): Promise<ClinicalTrialIntake> {
    const { data } = await apiClient.post<ClinicalTrialIntake>(
      `/clinical-trials/study-intakes/${intakeId}/analyze`
    );
    return data;
  }

  async function updateStudyIntakeDraft(
    intakeId: string,
    draft: ClinicalTrialStudyDraft
  ): Promise<ClinicalTrialIntake> {
    const { data } = await apiClient.patch<ClinicalTrialIntake>(
      `/clinical-trials/study-intakes/${intakeId}/draft`,
      draft
    );
    return data;
  }

  async function confirmStudyIntake(
    intakeId: string,
    siteId?: string | null
  ): Promise<ClinicalTrialIntake> {
    const { data } = await apiClient.post<ClinicalTrialIntake>(
      `/clinical-trials/study-intakes/${intakeId}/confirm`,
      siteId ? { siteId } : {},
      { timeout: 0 }
    );
    return data;
  }

  return {
    createStudyIntake,
    analyzeStudyIntake,
    confirmStudyIntake,
    updateStudyIntakeDraft,
    updateManagedStudyCriteria,
    updateManagedStudyRecruitment,
    updateManagedStudyShortTitle,
    updateManagedStudyContact,
    updateManagedStudyVisibility,
    assignStudyToInvestigator,
    unassignStudyFromInvestigator,
    createPosterSubmission,
    updateMedicalRepositoryStudy,
  };
}
